// features/workstations/dashboard-sections/flagged-companies.js
// ═══════════════════════════════════════════════════════════
// FLAGGED COMPANIES — Tier D (long-term workspace)
// ═══════════════════════════════════════════════════════════
// The triage queue. Companies that hit the radar but haven't
// earned a thesis or watchlist seat yet.
//
//   new        ─ just flagged, hasn't been touched yet
//   reviewing  ─ user has started diligence
//   promoted   ─ became a watchlist / thesis (audit trail only)
//   dismissed  ─ not for me
//
// V1 is manual + status transitions. Engine 6 (Possibility) will
// later write directly into this table when it spots companies
// matching the user's criteria — same widget, no UI change needed.
//
// Action layer is deliberately small: status pills are click-to-
// transition, ticker is the headline, reason is the body. No
// edit-modal in V1; if a user wants to tweak the reason they can
// dismiss + re-add (or use PATCH directly later).
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PractitionerLens,
  MetaField,
} from '../../../components/widgets/primitives';

const STATUS_TONES = {
  new:        'gold',
  reviewing:  'amber',
  promoted:   'sage',
  dismissed:  'steel',
};

const STATUS_LABELS = {
  new:        'new',
  reviewing:  'reviewing',
  promoted:   'promoted',
  dismissed:  'dismissed',
};

const SOURCE_LABELS = {
  manual:     'manual',
  engine:     'engine',
  news:       'news',
  screener:   'screener',
  community:  'community',
};

// Filter chips — only the active flow statuses by default.
// "all" lets the user see everything including dismissed/promoted
// for an audit trail.
const FILTER_OPTIONS = [
  { key: 'active',     label: 'active',     statuses: ['new', 'reviewing'] },
  { key: 'new',        label: 'new only',   statuses: ['new'] },
  { key: 'reviewing',  label: 'reviewing',  statuses: ['reviewing'] },
  { key: 'promoted',   label: 'promoted',   statuses: ['promoted'] },
  { key: 'dismissed',  label: 'dismissed',  statuses: ['dismissed'] },
  { key: 'all',        label: 'all',        statuses: null },
];

// ═══════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════
export default function FlaggedCompaniesSection({ workstation, userId }) {
  const [items, setItems] = useState([]);
  const [tally, setTally] = useState({ total: 0, by_status: { new: 0, reviewing: 0, promoted: 0, dismissed: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('active');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        workstation_id: workstation.id,
        user_id: userId,
      });
      const res = await fetch(`/api/workstations/flagged-companies?${qs}`, { cache: 'no-store' });
      const payload = res.ok ? await res.json() : null;
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      setItems(Array.isArray(payload?.items) ? payload.items : []);
      setTally(payload?.tally || { total: 0, by_status: { new: 0, reviewing: 0, promoted: 0, dismissed: 0 } });
    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId]);

  useEffect(() => { load(); }, [load]);

  const visibleItems = useMemo(() => {
    const opt = FILTER_OPTIONS.find(o => o.key === filter) || FILTER_OPTIONS[0];
    if (!opt.statuses) return items;
    return items.filter(it => opt.statuses.includes(it.status));
  }, [items, filter]);

  const updateStatus = useCallback(async (id, nextStatus) => {
    try {
      const res = await fetch('/api/workstations/flagged-companies', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, user_id: userId, status: nextStatus }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || `HTTP ${res.status}`);
        return;
      }
      setItems((prev) => prev.map(it => it.id === id ? data.item : it));
      // Recompute tally locally to avoid a round-trip
      setTally((prev) => {
        const next = { ...prev, by_status: { ...prev.by_status } };
        const old = items.find(it => it.id === id);
        if (old?.status && next.by_status[old.status] != null) next.by_status[old.status] -= 1;
        if (next.by_status[nextStatus] != null) next.by_status[nextStatus] += 1;
        return next;
      });
    } catch (e) {
      setError(e?.message || 'Update failed');
    }
  }, [userId, items]);

  const removeItem = useCallback(async (id) => {
    try {
      const qs = new URLSearchParams({ id, user_id: userId });
      const res = await fetch(`/api/workstations/flagged-companies?${qs}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data?.error || `HTTP ${res.status}`);
        return;
      }
      setItems((prev) => prev.filter(it => it.id !== id));
      setTally((prev) => {
        const next = { ...prev, by_status: { ...prev.by_status } };
        const removed = items.find(it => it.id === id);
        if (removed?.status && next.by_status[removed.status] != null) {
          next.by_status[removed.status] -= 1;
        }
        next.total = Math.max(0, next.total - 1);
        return next;
      });
    } catch (e) {
      setError(e?.message || 'Delete failed');
    }
  }, [userId, items]);

  const addItem = useCallback(async (ticker, reason) => {
    try {
      const res = await fetch('/api/workstations/flagged-companies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id: workstation.id,
          user_id: userId,
          ticker,
          reason,
          source: 'manual',
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || `HTTP ${res.status}`);
        return false;
      }
      setItems((prev) => [data.item, ...prev]);
      setTally((prev) => ({
        total: prev.total + 1,
        by_status: { ...prev.by_status, new: (prev.by_status.new || 0) + 1 },
      }));
      return true;
    } catch (e) {
      setError(e?.message || 'Add failed');
      return false;
    }
  }, [workstation?.id, userId]);

  // ─── Render ───
  return (
    <section className="ws-flagged">
      <Eyebrow parts={[
        'flagged companies',
        tally.total > 0 ? `${tally.total} total` : null,
        tally.by_status.new > 0 ? `${tally.by_status.new} new` : null,
        tally.by_status.reviewing > 0 ? `${tally.by_status.reviewing} reviewing` : null,
      ]} />

      <SerifHeadline>{headlineFor(tally)}</SerifHeadline>
      <SerifSummary>{summaryFor(tally)}</SerifSummary>

      {/* Filter row */}
      <div className="ws-flagged-filters">
        {FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            type="button"
            className={`ws-flagged-filter ${filter === opt.key ? 'is-active' : ''}`}
            onClick={() => setFilter(opt.key)}
          >
            {opt.label}
          </button>
        ))}
        <button
          type="button"
          className={`ws-flagged-add-btn ${adding ? 'is-active' : ''}`}
          onClick={() => setAdding(v => !v)}
        >
          {adding ? 'cancel' : '+ flag a company'}
        </button>
      </div>

      {adding && (
        <AddRow
          onAdd={async (ticker, reason) => {
            const ok = await addItem(ticker, reason);
            if (ok) setAdding(false);
          }}
        />
      )}

      {loading && <div className="ws-flagged-loading">Loading flagged companies…</div>}

      {!loading && visibleItems.length === 0 && (
        <EmptyState filter={filter} totalAll={tally.total} error={error} />
      )}

      {!loading && visibleItems.length > 0 && (
        <SectionStrip label={`showing ${filter}`} count={visibleItems.length}>
          <div className="ws-flagged-list">
            {visibleItems.map((it) => (
              <FlaggedCard
                key={it.id}
                item={it}
                onStatus={updateStatus}
                onDelete={removeItem}
              />
            ))}
          </div>
        </SectionStrip>
      )}

      {error && <div className="ws-flagged-error">{error}</div>}

      <PractitionerLens label="how to use this">
        Flag anything that catches your eye — earnings beat, interesting
        moat, competitor's customer migration. The triage queue. Move
        to <em>reviewing</em> when you start diligence. Promote when
        you've stated a thesis. Dismiss when it's clearly not a fit.
        Engine 6 will later flag companies automatically against your
        criteria; the workflow stays identical.
      </PractitionerLens>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// ADD ROW
// ═══════════════════════════════════════════════════════════
function AddRow({ onAdd }) {
  const [ticker, setTicker] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const t = ticker.trim().toUpperCase();
    const r = reason.trim();
    if (!t || !r || busy) return;
    setBusy(true);
    await onAdd(t, r);
    setBusy(false);
    setTicker('');
    setReason('');
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="ws-flagged-add-row">
      <input
        type="text"
        className="ws-flagged-add-ticker"
        placeholder="TICKER"
        value={ticker}
        maxLength={16}
        onChange={(e) => setTicker(e.target.value.toUpperCase())}
        onKeyDown={onKey}
        disabled={busy}
      />
      <textarea
        className="ws-flagged-add-reason"
        placeholder="Why is this on your radar? (cmd+enter to flag)"
        value={reason}
        rows={2}
        maxLength={2000}
        onChange={(e) => setReason(e.target.value)}
        onKeyDown={onKey}
        disabled={busy}
      />
      <button
        type="button"
        className="ws-flagged-add-submit"
        onClick={submit}
        disabled={!ticker.trim() || !reason.trim() || busy}
      >
        {busy ? '…' : 'flag'}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CARD
// ═══════════════════════════════════════════════════════════
function FlaggedCard({ item, onStatus, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const tone = STATUS_TONES[item.status] || 'steel';
  const statusLabel = STATUS_LABELS[item.status] || item.status;
  const sourceLabel = SOURCE_LABELS[item.source] || item.source;

  const flaggedAgo = ago(item.flagged_at);

  // Available transitions per current status
  const transitions = nextTransitions(item.status);

  return (
    <div className={`ws-flagged-card ws-flagged-card-${tone}`}>
      <button
        type="button"
        className="ws-flagged-card-head"
        onClick={() => setExpanded(v => !v)}
        title={expanded ? 'Collapse' : 'Expand'}
      >
        <div className="ws-flagged-card-head-top">
          <div className="ws-flagged-ticker-block">
            <span className="ws-flagged-ticker">{item.ticker}</span>
            {item.company_name && (
              <span className="ws-flagged-company">{item.company_name}</span>
            )}
          </div>
          <div className="ws-flagged-card-meta">
            <span className={`ws-flagged-status ws-flagged-status-${tone}`}>{statusLabel}</span>
            <span className="ws-flagged-source">{sourceLabel}</span>
            <span className="ws-flagged-age">{flaggedAgo}</span>
          </div>
        </div>
        <div className="ws-flagged-reason">
          {expanded || item.reason.length <= 220
            ? item.reason
            : `${item.reason.slice(0, 220)}…`}
        </div>
      </button>

      {expanded && (
        <div className="ws-flagged-drawer">
          {item.metadata && Object.keys(item.metadata).length > 0 && (
            <MetaField label="snapshot">
              {Object.entries(item.metadata)
                .filter(([_, v]) => v != null && v !== '')
                .map(([k, v]) => `${k}: ${String(v)}`)
                .join(' · ') || '—'}
            </MetaField>
          )}
          <MetaField label="flagged">{flaggedAgo} · via {sourceLabel}</MetaField>
          {item.reviewed_at && (
            <MetaField label="reviewing since">{ago(item.reviewed_at)}</MetaField>
          )}
          {item.promoted_at && (
            <MetaField label="promoted">{ago(item.promoted_at)}</MetaField>
          )}
          {item.dismissed_at && (
            <MetaField label="dismissed">{ago(item.dismissed_at)}</MetaField>
          )}

          <div className="ws-flagged-actions">
            {transitions.map((nextStatus) => (
              <button
                key={nextStatus}
                type="button"
                className={`ws-flagged-action ws-flagged-action-${STATUS_TONES[nextStatus]}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onStatus(item.id, nextStatus);
                }}
              >
                → {STATUS_LABELS[nextStatus]}
              </button>
            ))}
            <button
              type="button"
              className="ws-flagged-action ws-flagged-action-delete"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Delete the flag on ${item.ticker}? This removes the audit trail.`)) {
                  onDelete(item.id);
                }
              }}
            >
              delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// EMPTY STATE
// ═══════════════════════════════════════════════════════════
function EmptyState({ filter, totalAll, error }) {
  const isFiltered = filter !== 'all' && totalAll > 0;
  return (
    <div className="ws-flagged-empty">
      {isFiltered ? (
        <>
          <SerifHeadline>Nothing in this filter.</SerifHeadline>
          <SerifSummary>
            You have {totalAll} total flagged compan{totalAll === 1 ? 'y' : 'ies'} —
            switch the filter above to see them.
          </SerifSummary>
        </>
      ) : (
        <>
          <SerifHeadline>Nothing on the radar yet.</SerifHeadline>
          <SerifSummary>
            Flag a company when it catches your eye — an interesting earnings
            beat, a moat you want to study, a competitor mention. The flag
            queue is the triage layer before a thesis gets stated.
          </SerifSummary>
        </>
      )}
      {error && <div className="ws-flagged-error">{error}</div>}
    </div>
  );
}

// ─── Helpers ───
function headlineFor(tally) {
  if (tally.total === 0) return 'Nothing flagged yet.';
  if (tally.by_status.new > 0) {
    return `${tally.by_status.new} new flag${tally.by_status.new === 1 ? '' : 's'} waiting on triage.`;
  }
  if (tally.by_status.reviewing > 0) {
    return `${tally.by_status.reviewing} compan${tally.by_status.reviewing === 1 ? 'y' : 'ies'} in active review.`;
  }
  return `${tally.total} compan${tally.total === 1 ? 'y' : 'ies'} on file.`;
}

function summaryFor(tally) {
  if (tally.total === 0) {
    return 'Anything that catches your eye but doesn’t yet warrant a thesis lives here. Flag it, triage it later.';
  }
  if (tally.by_status.new > 0) {
    return 'Open the new flags and decide: start reviewing, or dismiss if it’s not a fit.';
  }
  if (tally.by_status.reviewing > 0) {
    return 'These are mid-diligence. Promote when you’ve formed a thesis; dismiss if the work didn’t pan out.';
  }
  return 'Audit trail of past flags. Use the filter to focus on active items.';
}

function nextTransitions(status) {
  switch (status) {
    case 'new':       return ['reviewing', 'dismissed'];
    case 'reviewing': return ['promoted', 'dismissed', 'new'];
    case 'promoted':  return ['reviewing'];
    case 'dismissed': return ['new', 'reviewing'];
    default:          return ['reviewing', 'dismissed'];
  }
}

function ago(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diffMs = now - d.getTime();
    if (diffMs < 60000) return 'just now';
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
    if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`;
    if (diffMs < 86400000 * 7) return `${Math.floor(diffMs / 86400000)}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}
